import { useChatStatus } from '@ai-sdk-tools/store';
import { DetailCardV2 } from '@newfront-insurance/core-ui';
import { ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import { Streamdown, type Components } from 'streamdown';

import { preprocessMarkdownCitations } from '../../../lib/preprocess-citations';
import { remarkPlugins, shikiTheme, streamdownPlugins } from '../../../lib/streamdown-config';
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationSource,
  InlineCitationText,
} from '../../ai-elements';
import { type TableData } from '../../markdown-table/lib';
import { TableWrapper } from '../../markdown-table/table-wrapper';
import {
  type DataExplorerSourceMetadata,
  getDataExplorerSourceMetadata,
  getSourceMetadata,
  type SourcesMap,
} from '../lib/sources';

interface MarkdownWithCitationsProps {
  children: string;
  sourcesMap: SourcesMap;
  messageUuid?: string;
  className?: string;
  /** Callback when user clicks expand on a table (opens in spreadsheet view) */
  onExpandTable?: (tableData: TableData) => void;
  /** Prefix for analytics event names */
  prefix?: string;
  /** Whether to render data explorer-specific citation UI */
  enableDataExplorerSources?: boolean;
}

/**
 * Citation component rendered from preprocessed <citation> HTML tags.
 * Receives sourceId from the AST node and looks up source data from the map.
 */
interface CitationComponentProps {
  sourceId: string;
  sourcesMap: SourcesMap;
  sourceIndexMap: Map<string, number>;
  enableDataExplorerSources?: boolean;
}

function formatSnakeCaseLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface DataExplorerCitationContentProps {
  title: string;
  url: string;
  metadata: DataExplorerSourceMetadata;
}

function DataExplorerCitationContent({ title, url, metadata }: DataExplorerCitationContentProps) {
  return (
    <DetailCardV2
      title={title}
      eyebrowProps={{ text: metadata.entityType }}
      defaultExpanded
      isExpandable={false}
      cardProps={{ cardClassName: 'border-none shadow-none p-0 hover:bg-transparent' }}
      actions={[
        <a
          key="link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-primary transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-4 w-4" />
        </a>,
      ]}
    >
      {metadata.preview.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-xs">
          {metadata.preview.map(({ key, value }) => (
            <li key={key} className="flex items-baseline gap-3">
              <span className="text-muted-foreground w-24 shrink-0">{formatSnakeCaseLabel(key)}</span>
              <span className="font-medium">{value}</span>
            </li>
          ))}
        </ul>
      )}
    </DetailCardV2>
  );
}

function CitationComponent({
  sourceId,
  sourcesMap,
  sourceIndexMap,
  enableDataExplorerSources,
}: CitationComponentProps) {
  const source = sourcesMap.get(sourceId);

  if (!source) {
    return null;
  }

  const metadata = getSourceMetadata(source);
  const dataExplorerMetadata = getDataExplorerSourceMetadata(source);
  const index = sourceIndexMap.get(sourceId) ?? 0;
  const isDataExplorer = enableDataExplorerSources && !!dataExplorerMetadata;

  return (
    <InlineCitation>
      <InlineCitationText />
      <InlineCitationCard>
        <InlineCitationCardTrigger sources={[source.url]}>{index}</InlineCitationCardTrigger>
        <InlineCitationCardBody>
          <InlineCitationCarousel>
            <InlineCitationCarouselContent>
              <InlineCitationCarouselItem>
                {isDataExplorer ? (
                  <DataExplorerCitationContent title={source.title} url={source.url} metadata={dataExplorerMetadata} />
                ) : (
                  <InlineCitationSource title={source.title} description={metadata?.textSnippet}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary absolute top-0 right-0 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    {metadata?.fileDate && (
                      <div className="text-muted-foreground text-xs">
                        Date: {new Date(metadata.fileDate).toLocaleDateString()}
                      </div>
                    )}
                    {metadata?.emailFrom && (
                      <div className="text-muted-foreground text-xs">From: {metadata.emailFrom}</div>
                    )}
                    {metadata?.pageNumber && (
                      <div className="text-muted-foreground text-xs">Page: {String(metadata.pageNumber)}</div>
                    )}
                    {metadata?.sectionTitle && (
                      <div className="text-muted-foreground text-xs">Section: {String(metadata.sectionTitle)}</div>
                    )}
                  </InlineCitationSource>
                )}
              </InlineCitationCarouselItem>
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
}

/**
 * Create component overrides for markdown with citations.
 * Uses preprocessed <citation> tags (via preprocessMarkdownCitations) that are
 * handled at the AST level, so we only need minimal component overrides.
 */
function createComponents(
  sourcesMap: SourcesMap,
  sourceIndexMap: Map<string, number>,
  messageUuid?: string,
  onExpandTable?: (tableData: TableData) => void,
  prefix?: string,
  enableDataExplorerSources?: boolean,
): Partial<Components> {
  return {
    // Citation elements created by preprocessMarkdownCitations or output directly by the LLM
    // @ts-expect-error - 'citation' is a custom element not in standard Components
    citation: ({ sourceid }: { sourceid: string }) => (
      <CitationComponent
        sourceId={sourceid}
        sourcesMap={sourcesMap}
        sourceIndexMap={sourceIndexMap}
        enableDataExplorerSources={enableDataExplorerSources}
      />
    ),
    // Links use Next.js Link for client-side navigation
    a: ({ children, href, ...props }) => (
      <Link className="text-brand-400 hover:underline" target="_blank" rel="noreferrer" href={href ?? '#'} {...props}>
        {children}
      </Link>
    ),
    // Table with analytics + expand callback
    table: ({ children, ...props }) => (
      <TableWrapper onExpandTable={onExpandTable} prefix={prefix} messageUuid={messageUuid} {...props}>
        {children}
      </TableWrapper>
    ),
  };
}

// Allow custom <citation> HTML tags through the sanitizer with their attributes
// Note: must use lowercase 'sourceid' because HTML parsers lowercase all attributes
const allowedTags = { citation: ['sourceid'] };

function NonMemoizedMarkdownWithCitations({
  children,
  sourcesMap,
  messageUuid,
  className,
  onExpandTable,
  prefix,
  enableDataExplorerSources,
}: MarkdownWithCitationsProps) {
  const status = useChatStatus();
  const isStreaming = status === 'streaming';

  // Pre-process markdown to convert reference-style citation links
  const processedMarkdown = useMemo(() => preprocessMarkdownCitations(children), [children]);

  // Create sources array (preserves insertion order)
  const sourcesArray = useMemo(() => Array.from(sourcesMap.values()), [sourcesMap]);

  // Create sourceId -> 1-based index map for O(1) lookup
  const sourceIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sourcesArray.forEach((source, index) => {
      map.set(source.sourceId, index + 1);
    });
    return map;
  }, [sourcesArray]);

  // Memoize components to avoid recreation on every render
  const components = useMemo(
    () => createComponents(sourcesMap, sourceIndexMap, messageUuid, onExpandTable, prefix, enableDataExplorerSources),
    [sourcesMap, sourceIndexMap, messageUuid, onExpandTable, prefix, enableDataExplorerSources],
  );

  return (
    <Streamdown
      plugins={streamdownPlugins}
      remarkPlugins={remarkPlugins}
      shikiTheme={shikiTheme}
      allowedTags={allowedTags}
      components={components}
      className={className}
      isAnimating={isStreaming}
      animated={isStreaming}
    >
      {processedMarkdown}
    </Streamdown>
  );
}

function areSourcesMapsEqual(prev: SourcesMap, next: SourcesMap): boolean {
  if (prev === next) return true;
  if (prev.size !== next.size) return false;

  return Array.from(prev.entries()).every(([sourceId, prevSource]) => {
    const nextSource = next.get(sourceId);
    if (!nextSource) return false;
    if (prevSource.url !== nextSource.url || prevSource.title !== nextSource.title) return false;

    const prevMetadata = getSourceMetadata(prevSource);
    const nextMetadata = getSourceMetadata(nextSource);

    return (
      prevMetadata?.textSnippet === nextMetadata?.textSnippet &&
      prevMetadata?.fileDate === nextMetadata?.fileDate &&
      prevMetadata?.emailFrom === nextMetadata?.emailFrom
    );
  });
}

export const MarkdownWithCitations = memo(
  NonMemoizedMarkdownWithCitations,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    areSourcesMapsEqual(prevProps.sourcesMap, nextProps.sourcesMap) &&
    prevProps.messageUuid === nextProps.messageUuid &&
    prevProps.className === nextProps.className &&
    prevProps.enableDataExplorerSources === nextProps.enableDataExplorerSources,
);
